import React, {
  FunctionComponent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { acceptTermsNew } from 'Redux/actions/auth'
import { getAreTermsAccepted, getTermsPosition } from 'Redux/selectors/auth'
import CheckboxAntd from 'Containers/ui/CheckboxAntd'
import { Desktop, Mobile } from 'Shared/components/Responsive'
import { confetti } from 'Shared/img/guidelineForCurators'
import { termsAndConditions as termsAndConditionsLink } from 'Constants/links'

import { useSlidesContent } from './useSlidesContent'
import {
  Button,
  ButtonBack,
  ButtonsContainer,
  CheckboxContainer,
  Confetti,
  ContentContainer,
  Image,
  inlineStyles,
  Link,
  ModalContainer,
  ObservableContainer,
  PaginationContainer,
  Paginator,
  Text,
  Title,
} from './styles'
import { TermsAndConditionsProps } from './props'

export const TermsAndConditions: FunctionComponent<TermsAndConditionsProps> = ({
  toggleContent,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { slides } = useSlidesContent()

  const [modalVisible, setModalVisible] = useState(false)
  const [currentSlideId, setCurrentSlideId] = useState(0)
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false)
  const [isContentViewed, setIsContentViewed] = useState(false)
  const [lastSlideViewed, setLastSlideViewed] = useState(1)

  const observableTopRef = useRef<HTMLDivElement>(null)
  const observableBotRef = useRef<HTMLDivElement>(null)
  const didMount = useRef<boolean>(false)

  const areTermsAccepted: boolean = useSelector(getAreTermsAccepted)

  const termsPosition: number = useSelector(getTermsPosition)

  const observerOptions = {
    root: null,
    rootMargin: '0px',
    treshold: [1],
  }

  const sendNextPageViewed = async () =>
    dispatch(acceptTermsNew(lastSlideViewed, slides.length - 2))

  useEffect(() => {
    if (!areTermsAccepted) {
      setModalVisible(true)
      toggleContent(false)
    }
    setLastSlideViewed(termsPosition)
    if (termsPosition > 1) {
      setIsContentViewed(true)
    }
  }, [areTermsAccepted, termsPosition])

  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true
    } else if (currentSlideId === slides.length - 1 && !isContentViewed) {
      toggleContent(true)
      setModalVisible(false)
    } else if (!isContentViewed) {
      setCurrentSlideId(currentSlideId + 1)
      if (currentSlideId + 1 <= lastSlideViewed) {
        setIsContentViewed(true)
      }
      sendNextPageViewed().then(() => {
        if (lastSlideViewed <= currentSlideId) {
          setLastSlideViewed(lastSlideViewed + 1)
        }
      })
    }
  }, [isContentViewed])

  // @ts-ignore
  const unlockSlideSubmit = ([entry]) => {
    if (!isContentViewed && entry.isIntersecting) {
      setIsContentViewed(true)
    }
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      // @ts-ignore
      unlockSlideSubmit,
      observerOptions
    )

    if (observableBotRef.current) observer.observe(observableBotRef.current)
    return () => {
      if (observableBotRef.current) observer.unobserve(observableBotRef.current)
    }
  }, [observableBotRef, observerOptions])

  const goToNextSlide = () => {
    setIsContentViewed(false)
    observableTopRef?.current?.scrollIntoView()
  }

  const goToPrevSlide = () => {
    observableTopRef?.current?.scrollIntoView()
    setCurrentSlideId(currentSlideId - 1)
  }

  const memoCurrentSlide = useMemo(() => slides[currentSlideId], [
    currentSlideId,
  ])

  const renderPagination = useMemo(
    () =>
      slides.map(
        (el, id) =>
          el.id !== slides[0].id &&
          el.id !== slides[slides.length - 1].id && (
            <Paginator id={`${el.id}`} active={id === currentSlideId} />
          )
      ),
    [currentSlideId]
  )

  const paginatableSlides = useMemo(
    () => currentSlideId !== 0 && currentSlideId !== 6,
    [currentSlideId]
  )

  const submitDisabled =
    (currentSlideId === 5 && !isCheckboxChecked) ||
    (!isContentViewed &&
      (lastSlideViewed <= currentSlideId || areTermsAccepted))

  return (
    <>
      <Desktop>
        <ModalContainer
          visible={modalVisible}
          centered
          maskStyle={inlineStyles.modalMask}
        >
          <ContentContainer>
            <ObservableContainer ref={observableTopRef} />
            {currentSlideId === 6 && <Confetti src={confetti} />}
            <Image src={memoCurrentSlide.img} />
            {currentSlideId !== 6 && <Title>{memoCurrentSlide.title}</Title>}
            {memoCurrentSlide.content}
            {currentSlideId === 5 && (
              <CheckboxContainer>
                <CheckboxAntd
                  checked={isCheckboxChecked}
                  onChange={e => setIsCheckboxChecked(e.target.checked)}
                />
                <Text monospace>
                  {t('termsAndConditions.checkboxText')}
                  <Link target="_blank" href={termsAndConditionsLink}>
                    {t('termsAndConditions.checkboxLink')}
                  </Link>
                </Text>
              </CheckboxContainer>
            )}
            <ObservableContainer ref={observableBotRef} />
            {paginatableSlides && (
              <PaginationContainer>{renderPagination}</PaginationContainer>
            )}
            <ButtonsContainer>
              {paginatableSlides && (
                <ButtonBack onClick={goToPrevSlide}>
                  {t('termsAndConditions.back')}
                </ButtonBack>
              )}
              <Button disabled={submitDisabled} onClick={goToNextSlide}>
                {currentSlideId === 0
                  ? t('termsAndConditions.takeALook')
                  : t('termsAndConditions.continue')}
              </Button>
            </ButtonsContainer>
          </ContentContainer>
        </ModalContainer>
      </Desktop>
      <Mobile>
        {modalVisible && (
          <ContentContainer>
            <ObservableContainer top ref={observableTopRef} />
            {currentSlideId === 6 && <Confetti src={confetti} />}
            <Image src={memoCurrentSlide.img} />
            <Title>{memoCurrentSlide.title}</Title>
            {memoCurrentSlide.content}
            {currentSlideId === 5 && (
              <CheckboxContainer>
                <CheckboxAntd
                  checked={isCheckboxChecked}
                  onChange={e => setIsCheckboxChecked(e.target.checked)}
                />
                <Text monospace>
                  {t('termsAndConditions.checkboxText')}
                  <Link target="_blank" href={termsAndConditionsLink}>
                    {t('termsAndConditions.checkboxLink')}
                  </Link>
                </Text>
              </CheckboxContainer>
            )}
            <ObservableContainer ref={observableBotRef} />
            {paginatableSlides && (
              <PaginationContainer>{renderPagination}</PaginationContainer>
            )}
            <ButtonsContainer>
              {paginatableSlides && (
                <ButtonBack onClick={goToPrevSlide}>
                  {t('termsAndConditions.back')}
                </ButtonBack>
              )}
              <Button disabled={submitDisabled} onClick={goToNextSlide}>
                {currentSlideId === 0
                  ? t('termsAndConditions.takeALook')
                  : t('termsAndConditions.continue')}
              </Button>
            </ButtonsContainer>
          </ContentContainer>
        )}
      </Mobile>
    </>
  )
}
